import { router, usePage } from "@inertiajs/vue3";
import { computed, type ComputedRef, type Ref } from "vue";

import type { ActionConfig, SharedCrudData } from "../types";
import { edit } from "../wayfinder/routes";

/**
 * Group actions by their group property for rendering with separators.
 * Returns an array of [groupKey, actions[]] tuples sorted by priority.
 *
 * This is a standalone utility function that can be used without the full useActions composable.
 */
export function groupActionsByGroup(
  actions: ActionConfig[]
): [string, ActionConfig[]][] {
  const groups = new Map<string, ActionConfig[]>();

  for (const action of actions) {
    const groupKey = action.group ?? "default";
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(action);
  }

  // Sort groups by minimum priority
  const entries = Array.from(groups.entries());
  entries.sort((a, b) => {
    const aPriority = Math.min(...a[1].map((action) => action.priority ?? 0));
    const bPriority = Math.min(...b[1].map((action) => action.priority ?? 0));
    return aPriority - bPriority;
  });

  return entries;
}

/** Context object for resolving action placeholders */
export interface ActionContext {
  model: string;
  id?: string | number;
  ids?: (string | number)[];
  item?: Record<string, unknown>;
  editUrl?: string;
}

/** Options for useActions composable */
export interface UseActionsOptions {
  emit?: (event: string, payload: unknown) => void;
}

/**
 * Composable for action filtering by permissions and execution by type.
 *
 * Handles:
 * - Permission filtering (hide actions user can't perform)
 * - Grouping for separators
 * - Action execution based on type (api, navigate, tab, event)
 */
export function useActions(
  actions: Ref<ActionConfig[]> | ComputedRef<ActionConfig[]>,
  context: Ref<ActionContext> | ComputedRef<ActionContext>,
  options: UseActionsOptions = {}
) {
  const page = usePage();
  const crud = computed(() => (page.props as { crud?: SharedCrudData }).crud);
  const permissions = computed(() => crud.value?.active?.permissions ?? {});

  /**
   * Check if user has a specific permission.
   */
  function hasPermission(permissionKey: string | null | undefined): boolean {
    if (!permissionKey) return true;
    return permissions.value[permissionKey] === true;
  }

  /**
   * Filter actions by permission and hidden flag.
   */
  const visibleActions = computed(() =>
    actions.value.filter((a) => !a.hidden && hasPermission(a.permission))
  );

  /**
   * Group actions by their group property for rendering separators.
   * Returns a Map of group name → actions in that group.
   */
  const groupedActions = computed(() => {
    const groups = new Map<string, ActionConfig[]>();

    for (const action of visibleActions.value) {
      const groupKey = action.group ?? "default";
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(action);
    }

    return groups;
  });

  /**
   * Get groups in order for rendering (sorted by first action's priority).
   */
  const orderedGroups = computed(() => {
    const entries = Array.from(groupedActions.value.entries());

    // Sort groups by the minimum priority of their actions
    entries.sort((a, b) => {
      const aPriority = Math.min(...a[1].map((action) => action.priority ?? 0));
      const bPriority = Math.min(...b[1].map((action) => action.priority ?? 0));
      return aPriority - bPriority;
    });

    return entries.map(([group, groupActions]) => ({
      group,
      actions: groupActions
    }));
  });

  /**
   * Resolve placeholders in a route string.
   * Supports: :model, :id
   */
  function resolveRoute(route: string, ctx: ActionContext): string {
    return route
      .replace(":model", ctx.model)
      .replace(":id", String(ctx.id ?? ""));
  }

  /**
   * Execute an action based on its type.
   */
  function executeAction(
    action: ActionConfig,
    ids: (string | number)[] = []
  ): void {
    const ctx = context.value;
    const targetIds = ids.length > 0 ? ids : ctx.id ? [ctx.id] : [];

    switch (action.type) {
      case "api": {
        const url = action.route
          ? resolveRoute(action.route, { ...ctx, id: targetIds[0] })
          : "";
        const method = (action.method?.toLowerCase() ?? "post") as
          | "post"
          | "patch"
          | "delete"
          | "put";

        if (method === "delete") {
          router.delete(url, { preserveScroll: true });
        } else if (method === "patch") {
          router.patch(url, { ids: targetIds }, { preserveScroll: true });
        } else {
          router.post(url, { ids: targetIds }, { preserveScroll: true });
        }
        break;
      }

      case "navigate": {
        const url = action.route
          ? resolveRoute(action.route, { ...ctx, id: targetIds[0] })
          : "";
        router.visit(url, { preserveScroll: true });
        break;
      }

      case "tab": {
        const baseUrl =
          ctx.editUrl ??
          edit({ model: ctx.model, id: String(targetIds[0] ?? ctx.id) }).url;
        const tabUrl = action.tabTarget
          ? `${baseUrl}?tab=${action.tabTarget}`
          : baseUrl;
        router.visit(tabUrl, { preserveScroll: true });
        break;
      }

      case "event": {
        if (options.emit && action.eventName) {
          options.emit(action.eventName, {
            ids: targetIds,
            payload: action.eventPayload ?? {}
          });
        }
        break;
      }

      default:
        console.warn(`Unknown action type: ${action.type}`);
    }
  }

  /**
   * Get an action by name from all actions (including hidden).
   */
  function getAction(name: string): ActionConfig | undefined {
    return actions.value.find((a) => a.name === name);
  }

  return {
    hasPermission,
    visibleActions,
    groupedActions,
    orderedGroups,
    executeAction,
    getAction
  };
}
